import { useEffect, useState } from 'react';
import {
  applyCadCanvasImageCors,
  prepareCadCanvasImageForMap,
} from '../utils/cadCanvasImage';

function readHint(hint) {
  const width = Number(hint?.width);
  const height = Number(hint?.height);
  if (!(width > 0) || !(height > 0)) return null;
  return { width, height };
}

/**
 * Ancho/alto naturales del PNG. `hint` pinta el contain ya, mientras carga.
 * CORS igual que el raster: si este `Image()` va sin `crossOrigin`, S3 queda
 * en caché sucio y el mapa a veces no puede leer píxeles.
 * Arranca el decode del canvas apenas hay URL (el mapa no espera a estar listo).
 * @param {string|null} url
 * @param {{ width?: number, height?: number }|null} [hint]
 * @returns {{ width: number, height: number }|null}
 */
export function useCadImageNaturalSize(url, hint = null) {
  const src = typeof url === 'string' ? url.trim() : '';
  const fallback = src ? readHint(hint) : null;
  const hintKey = fallback ? `${fallback.width}x${fallback.height}` : '';
  const [size, setSize] = useState(fallback);

  useEffect(() => {
    if (!src) {
      setSize(null);
      return undefined;
    }
    const known = hintKey
      ? { width: Number(hintKey.split('x')[0]), height: Number(hintKey.split('x')[1]) }
      : null;
    if (!known) setSize(null);
    let cancelled = false;
    const image = new Image();
    applyCadCanvasImageCors(image, src);
    image.onload = () => {
      if (cancelled) return;
      const width = Number(image.naturalWidth);
      const height = Number(image.naturalHeight);
      setSize(width > 0 && height > 0 ? { width, height } : null);
    };
    image.onerror = () => {
      if (!cancelled && !known) setSize(null);
    };
    image.src = src;
    void prepareCadCanvasImageForMap(src);
    return () => {
      cancelled = true;
    };
  }, [hintKey, src]);

  return size || fallback;
}
